import React from 'react';
import PropTypes from "prop-types";
import {serviceRetailerDropdown} from "../../utils/retailerDisplayFormatter";
import {useServiceBase} from "../Components/ReactTable/useServiceBase";
import Page from "../Components/Page";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import EditableReactTableV2 from "../Components/ReactTable/EditableReactTableV2";
import {INDEX_FIELD, RETAILER_ID_NAME_COLUMN} from "../Components/ReactTable/common_custom_columns";
import {
    EditFormBooleanDropdownInputField,
    EditFormDateInputField,
    EditFormDropdownInputField,
    EditFormNumberInputField
} from "../Components/ReactTable/EditRecordForm";
import {setValuesToNullIfEmptyOrUndefined} from "../Components/ReactTable/helpers";
import {sortAscendingWithNullsLast} from "../../utils/sorting";
import moment from "moment";

function CheckDigitRetailers({displayHelmetHeader = true, retailerId = undefined}) {
    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/checkDigitRetailers/${retailerId ? `?retailerId=${retailerId}` : ''}`
    const endpointUrlForPost = `${process.env.REACT_APP_EDP_API_BASE_URL}/checkDigitRetailers/`
    const {loading, data, onRowEditConfirmed, onRowDeleteConfirmed} = useServiceBase({endpointUrl, endpointUrlForPost});

    function convertStrDateToObject(useDate){
        return moment(useDate, 'YYYY-MM-DD').toDate();
    }

    function validatePreSave({newData, rows, form}) {
        const retailerId = newData.retailerId;
        const spinsStoreId = isNaN(newData.spinsStoreId) ? null : newData.spinsStoreId;
        const useFromDate = newData.useFromDate === '' ? null : newData.useFromDate;
        const useUntilDate = newData.useUntilDate === '' ? null : newData.useUntilDate;

        if(useFromDate){
            if(convertStrDateToObject(useFromDate) < convertStrDateToObject(useUntilDate)){
                return true;
            } else {
                form.setError('useFromDate', {type: 'custom', message: ['Must set a date before useUntilDate']});
                return false;
            }
        }

        const listOfRetailerIds = rows.filter(row => row.original?.retailerId === retailerId
            && ((row.original?.spinsStoreId === spinsStoreId) || (row.original?.spinsStoreId === null && spinsStoreId === null))
            && ((row.original?.useUntilDate === null && useUntilDate === null) || (row.original?.useUntilDate !== null && useUntilDate !== null))
            && row.original?.index !== newData.index
            && !(row.original?.useFromDate)
        )

        if(listOfRetailerIds.length > 0){
            if(!useUntilDate){
                form.setError('useUntilDate', {type: 'custom', message: ['Must set useUntilDate']});
                return false;
            }
            form.setError('useFromDate', {type: 'custom', message: ['Must set useFromDate']});
            return false;
        }

        return true;
    }

    function sortDatesWithNullsLast(datesA, datesB) {
        const numDatesA = datesA ? new Date(datesA) : null;
        const numDatesB = datesB ? new Date(datesB) : null;
        return sortAscendingWithNullsLast(numDatesA, numDatesB);
    }

    const columns = [
        RETAILER_ID_NAME_COLUMN,
        {header: "Spins Store Id", accessorKey: "spinsStoreId"},
        {header: "Sales Has Check Digit (UPC)", accessorKey: "salesHasCheckDigitForUPCs", size: 200},
        {header: "Sales Has Check Digit (PLU)", accessorKey: "salesHasCheckDigitForPLUs", size: 200},
        {header: "Items Has Check Digit (UPC)", accessorKey: "itemsHasCheckDigitForUPCs", size: 200},
        {header: "Items Has Check Digit (PLU)", accessorKey: "itemsHasCheckDigitForPLUs", size: 200},
        {header: "Use From Date", accessorKey: "useFromDate",
            sortingFn: (rowA, rowB) => sortDatesWithNullsLast(rowA.original?.useFromDate, rowB.original?.useFromDate)
        },
        {header: "Use Until Date", accessorKey: "useUntilDate",
            sortingFn: (rowA, rowB) => sortDatesWithNullsLast(rowA.original?.useUntilDate, rowB.original?.useUntilDate)
        }
    ];

    function onRowEditConfirmedWrapper(newOrUpdatedRow) {
        const sanitizedData = setValuesToNullIfEmptyOrUndefined(newOrUpdatedRow);
        return onRowEditConfirmed(sanitizedData);
    }

    return (<Page name={"Check Digit Retailers Configuration"} breadCrumbs={<Breadcrumbs items={[{label: "Configurations"}, {label: "Retailer"}, {label: "Check Digit"}]}/>} displayHelmetHeader={displayHelmetHeader}>
        <LoadingWrapper loading={loading}>
            <EditableReactTableV2
                filterable
                data={data.data}
                columns={columns}
                initialState={{sorting: [{id: "retailerIdName", desc: false}]}}
                editTable={[
                    INDEX_FIELD,
                    {id: 'retailerId', defaultValue: retailerId, Component: args => <EditFormDropdownInputField {...args} options={serviceRetailerDropdown().data} label="Retailer Id" disabled={retailerId !== undefined} isRequired={true} />},
                    {id: 'spinsStoreId', Component: args => <EditFormNumberInputField {...args} />},
                    {id: 'salesHasCheckDigitForUPCs', defaultValue: true, Component: args => <EditFormBooleanDropdownInputField {...args} />},
                    {id: 'salesHasCheckDigitForPLUs', defaultValue: true, Component: args => <EditFormBooleanDropdownInputField {...args} />},
                    {id: 'itemsHasCheckDigitForUPCs', defaultValue: true, Component: args => <EditFormBooleanDropdownInputField {...args} />},
                    {id: 'itemsHasCheckDigitForPLUs', defaultValue: true, Component: args => <EditFormBooleanDropdownInputField {...args} />},
                    {id: 'useFromDate', Component: args => <EditFormDateInputField {...args} />},
                    {id: 'useUntilDate', Component: args => <EditFormDateInputField {...args} />},
                ]}
                validatePreSave={validatePreSave}
                onRowEditConfirmed={onRowEditConfirmedWrapper}
                onRowDeleteConfirmed={onRowDeleteConfirmed}
            />
        </LoadingWrapper>
    </Page>);
}

CheckDigitRetailers.propTypes = {
    displayHelmetHeader: PropTypes.bool,
    retailerId: PropTypes.number
}

export default CheckDigitRetailers;